import {
  type ColumnStrategy,
  type ParsingStrategy,
  guessStrategy,
  parseIntoTable,
  parseTableWithStrategy,
} from '@/utils/parsing';
import type { Item } from '@/utils/store';
import { useMemo, useState } from 'react';

function ColumnParserStrategy({
  strategy,
  onChange,
}: {
  strategy: ParsingStrategy['columns'];
  onChange: (strategy: ParsingStrategy['columns']) => void;
}) {
  return (
    <tr>
      <th className="border border-b-4">Type</th>
      {strategy.map((item, i) => (
        <th className="border border-b-4" key={i}>
          <select
            value={item ?? 'null'}
            onChange={e => {
              const newStrategy = [...strategy];
              const strat = (() => {
                switch (e.target.value) {
                  case 'null':
                    return null;
                  case 'stockNumber':
                    return 'stockNumber';
                  case 'description':
                    return 'description';
                  default:
                    return Number(e.target.value);
                }
              })();
              newStrategy[i] = strat;
              onChange(newStrategy);
            }}
          >
            <option value="null">Ignore</option>
            <option value="stockNumber">Stock number</option>
            <option value="description">Description</option>
            <option value="0">Qty 1</option>
            <option value="1">Qty 2</option>
            <option value="2">Qty 3</option>
            <option value="3">Qty 4</option>
            <option value="4">Qty 5</option>
            <option value="5">Qty 6</option>
            <option value="6">Qty 7</option>
            <option value="7">Qty 8</option>
            <option value="8">Qty 9</option>
            <option value="9">Qty 10</option>
          </select>
        </th>
      ))}
    </tr>
  );
}

function ParserTable({
  table,
  strategy,
  onChange,
}: {
  table: string[][];
  strategy: ParsingStrategy['rows'];
  onChange: (strategy: ParsingStrategy['rows']) => void;
}) {
  return (
    <tbody>
      {table.map((row, i) => (
        <tr key={i}>
          <th className="border">
            <select
              value={strategy[i] ?? 'item'}
              onChange={e => {
                const newStrategy = [...strategy];
                newStrategy[i] = e.target.value as
                  | 'ignore'
                  | 'item'
                  | 'serials';
                onChange(newStrategy);
              }}
            >
              <option value="ignore">Ignore</option>
              <option value="item">Item</option>
              <option value="serials">Serials</option>
            </select>
          </th>
          {row.map((cell, j) => (
            <td key={j} className="border whitespace-pre">
              {cell || '\u00A0'}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export default function ParserPopup({
  input,
  onParse,
  onCancel,
}: {
  input: string;
  onParse: (output: Item[]) => void;
  onCancel: () => void;
}) {
  const table = useMemo(() => parseIntoTable(input), [input]);
  const [strategy, setStrategy] = useState<ParsingStrategy>(() =>
    guessStrategy(table),
  );

  const btns = (
    <div className="flex items-center justify-between">
      <button
        className="cursor-pointer rounded-lg bg-blue-300 p-2 px-4 py-2 hover:bg-blue-400"
        onClick={() => {
          const output = parseTableWithStrategy(table, strategy);
          onParse(output);
        }}
      >
        Parse
      </button>
      <button
        className="cursor-pointer rounded-lg bg-red-300 p-2 px-4 py-2 hover:bg-red-400"
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-10 overflow-auto bg-black/50 p-12">
      <div className="mx-auto flex w-fit flex-col gap-4 rounded-xl bg-white p-8">
        {btns}
        <table className="bg-white">
          <thead>
            <ColumnParserStrategy
              strategy={strategy.columns}
              onChange={columns =>
                setStrategy({
                  ...strategy,
                  columns,
                })
              }
            />
          </thead>
          <ParserTable
            table={table}
            strategy={strategy.rows}
            onChange={rows =>
              setStrategy({
                ...strategy,
                rows,
              })
            }
          />
        </table>
        {btns}
      </div>
    </div>
  );
}
